import { BoardTask } from '../types/task';

// How a change to one task turns the cached data into its next version. Each function takes the current list or
// board document and returns a new one with the change made, leaving the original untouched, with no React or
// network in them, so they can be tested directly. data/tasks.ts applies them to the cache.
//
// Two shapes hold tasks: the board's flat task list, and the board document, whose sections keep an ordered
// list of their task ids (that order is what the Board View displays).

type Section = { taskIds?: string[] } & Record<string, unknown>;
export type BoardDoc = { sectionsById?: Record<string, Section> } & Record<string, unknown>;

// --- the task list ---------------------------------------------------------------------------------------------

// Adding a task already in the list does nothing, so adding is safe to repeat.
export const addToList = (tasks: BoardTask[], task: BoardTask): BoardTask[] =>
    tasks.some((existing) => existing.id === task.id) ? tasks : [...tasks, task];

export const removeFromList = (tasks: BoardTask[], taskId: string): BoardTask[] =>
    tasks.filter((task) => task.id !== taskId);

export const removeSectionFromList = (tasks: BoardTask[], sectionId: string): BoardTask[] =>
    tasks.filter((task) => task.sectionId !== sectionId);

export const changeInList = (tasks: BoardTask[], taskId: string, changes: Partial<BoardTask>): BoardTask[] =>
    tasks.map((task) => (task.id === taskId ? { ...task, ...changes } : task));

// --- the board document's per-section ordering -----------------------------------------------------------------

// Sections that aren't in the cached board (or a board that isn't cached) are left alone.
const changeSection = (board: BoardDoc | undefined, sectionId: string, change: (taskIds: string[]) => string[]): BoardDoc | undefined => {
    const section = board?.sectionsById?.[sectionId];
    if (!board || !section) return board;

    return {
        ...board,
        sectionsById: { ...board.sectionsById, [sectionId]: { ...section, taskIds: change(section.taskIds ?? []) } },
    };
};

export const addToBoardSection = (board: BoardDoc | undefined, sectionId: string, taskId: string) =>
    changeSection(board, sectionId, (ids) => (ids.includes(taskId) ? ids : [...ids, taskId]));

export const removeFromBoardSection = (board: BoardDoc | undefined, sectionId: string, taskId: string) =>
    changeSection(board, sectionId, (ids) => ids.filter((id) => id !== taskId));

// A moved task goes to the bottom of its new section, as the server puts it. A task not in the old section's list is still added to
// the new one, and a task already in the new one is not added twice.
export const moveBetweenBoardSections = (board: BoardDoc | undefined, taskId: string, fromSectionId: string | undefined, toSectionId: string) => {
    const without = fromSectionId && fromSectionId !== toSectionId ? removeFromBoardSection(board, fromSectionId, taskId) : board;
    return fromSectionId === toSectionId ? board : addToBoardSection(without, toSectionId, taskId);
};
